import { logger } from '@rhg/libs/logger';
import { getPartnerCircleSponsorshipUpsertRequest } from '@rhg/utils/partner';
import {
    setStoredIdpToken,
    setStoredUserId,
    clearSAMLStorage,
    getSSOSessionStorageData,
} from '@rhg/utils/sso';
import { useEffect, useMemo } from 'react';
import { usePartnerByIdBase } from '../use-partner';
import { usePartnerEligibilityBase } from '../use-partner-eligibility';
import { getTokenData, useTokenData } from '../use-token-data';

export function useSSOFlow() {
    const {
        token,
        decodedToken,
        isSaml,
        isFetched: isTokenFetched,
        isError: isTokenError,
        error: tokenError,
    } = useTokenData();

    const {
        isFetched: isPartnerEligibilityFetched,
        isError: isPartnerEligibilityError,
        partnerError,
        eligibilityError,
        eligibility,
        isEligible,
        partnerId,
        partner,
        hasPreviouslyLinkedAccount,
    } = useSSOTokenPartnerEligibility(token);

    const isFetched =
        isTokenFetched &&
        (isPartnerEligibilityFetched || isPartnerEligibilityError);

    const isError = isTokenError || isPartnerEligibilityError;

    // Sets token storage if token is valid
    useEffect(() => {
        if (isSaml && !!token && !!decodedToken && !isTokenError) {
            setStoredUserId(decodedToken.sub!);
            setStoredIdpToken(token);
        }

        if (isTokenError) {
            clearSAMLStorage();
        }
    }, [decodedToken, isSaml, isTokenError, token]);

    return {
        isFetched,
        isError,
        tokenError,
        partnerError,
        eligibilityError,
        eligibility,
        isEligible,
        partnerId,
        partner,
        hasPreviouslyLinkedAccount,
    };
}

function useSSOTokenPartnerById(token?: string) {
    const tokenData = getTokenData(token);
    const { tokenPartnerPortalData } = tokenData ?? {};
    const partnerId = tokenPartnerPortalData?.partner_id;
    return usePartnerByIdBase({
        partnerId,
        getAccessTokenSilently: () => Promise.resolve(token ?? ''),
        isAuthenticated: !!token,
    });
}

export function useSSOTokenPartnerEligibility(token?: string) {
    const tokenData = getTokenData(token);
    const { isSaml, tokenPartnerPortalData } = tokenData ?? {};

    const tokenPartnerId = tokenPartnerPortalData?.partner_id;
    const hasPreviouslyLinkedAccount = !!token && !isSaml;

    const hasSsoDataClaim = !!tokenPartnerPortalData;
    const hasSubscriberId = !!tokenPartnerPortalData?.subscriber_id;

    // Diagnostic funnel marker (EVE-12589): absence of this marker in Datadog
    // means the SSO token never reached the eligibility hook (sessionStorage gone).
    useEffect(() => {
        if (!token) return;
        logger.info('sso.eligibility.inputs', {
            hasToken: !!token,
            isSaml: !!isSaml,
            hasSsoDataClaim,
            hasPartnerId: !!tokenPartnerId,
            hasSubscriberId,
            impliedWillBe: !hasPreviouslyLinkedAccount,
        });
    }, [
        token,
        isSaml,
        hasSsoDataClaim,
        tokenPartnerId,
        hasSubscriberId,
        hasPreviouslyLinkedAccount,
    ]);

    const {
        partner: partnerById,
        isFetched: isPartnerFetched,
        isError: isPartnerError,
        error: partnerError,
    } = useSSOTokenPartnerById(token);

    const request = useMemo(
        () =>
            isPartnerFetched
                ? getPartnerCircleSponsorshipUpsertRequest(
                      partnerById ?? undefined,
                      tokenPartnerPortalData
                  )
                : undefined,
        [isPartnerFetched, partnerById, tokenPartnerPortalData]
    );

    const requestBuilt = !!request;

    useEffect(() => {
        if (!token) return;
        logger.info('sso.eligibility.partnerFetch', {
            partnerQueryEnabled: !!token && !!tokenPartnerId,
            isPartnerFetched,
            isPartnerError,
            requestBuilt,
        });
    }, [token, tokenPartnerId, isPartnerFetched, isPartnerError, requestBuilt]);

    const {
        eligibility,
        isFetched: isEligibilityFetched,
        isError: isEligibilityError,
        error: eligibilityError,
    } = usePartnerEligibilityBase({
        partnerId: tokenPartnerId,
        request,
        getAccessTokenSilently: () => Promise.resolve(token ?? ''),
        isAuthenticated: !!token,
        isSSO: true,
        isImplied: !hasPreviouslyLinkedAccount,
    });

    const partner =
        eligibility?.partnerForSponsorship ??
        eligibility?.partnerForEligibility ??
        partnerById ??
        undefined;
    const partnerId = partner?.id;

    const isError = isPartnerError || isEligibilityError;
    const isEnabled = !!token;
    const isEligible = !!eligibility;

    useEffect(() => {
        if (partnerError) {
            logger.warn('sso - partner lookup error', {
                partnerId,
                partnerError: partnerError?.message,
            });
        }
    }, [partnerError, partnerId]);

    useEffect(() => {
        if (eligibilityError) {
            logger.warn('sso - eligibility error', {
                partnerId,
                isEligible,
                eligibilityError: eligibilityError?.message,
            });
        }
    }, [eligibilityError, partnerId, isEligible]);

    const isFetched =
        isEnabled &&
        (isPartnerFetched || isPartnerError) &&
        (isEligibilityFetched || isEligibilityError);

    // A token without a partner never starts the lookups, so isFetched would stay false forever
    const isPending = isEnabled && !!tokenPartnerId && !isFetched;

    useEffect(() => {
        if (!token || !isFetched) return;
        logger.info('sso.eligibility.resolved', {
            isEligible,
            isFetched,
            partnerId,
        });
    }, [token, isFetched, isEligible, partnerId]);

    return {
        isFetched,
        isPending,
        isEnabled,
        isError,
        partnerError,
        eligibilityError,
        eligibility,
        isEligible,
        partnerId,
        partner,
        hasPreviouslyLinkedAccount,
    };
}

export function useStoredSSOTokenPartnerEligibility() {
    const { ssoToken } = getSSOSessionStorageData();
    return useSSOTokenPartnerEligibility(ssoToken ?? undefined);
}
